import React, { useEffect, useMemo, useState } from "react";
import { useMobilePeerConnection } from "@/context/MobilePeerConnectionContext";
import MobileNetworkWarning from "./MobileNetworkWarning";
import MobileWebCamBeforeConnection from "./MobileWebCamBeforeConnection";
import MobileWebCamAfterConnection from "./MobileWebCamAfterConnection";
import MobileWebCamBypassModal from "./MobileWebCamBypassModal";
import type { HandDetector } from "@tensorflow-models/hand-pose-detection";
import type { ObjectDetection } from "@tensorflow-models/coco-ssd";

interface MobileWebCamSetupProps {
  instructions: string[];
  connectionUrl?: string;
  onSetupComplete?: (isComplete: boolean) => void;
  handDetector?: HandDetector | null;
  objectDetector?: ObjectDetection | null;
  detectionStatusMessage?: string;
  isDetectionPreparing?: boolean;
  detectionError?: Error | null;
  footer?: React.ReactNode;
}

const MobileWebCamSetup: React.FC<MobileWebCamSetupProps> = ({
  instructions: _instructions,
  connectionUrl,
  onSetupComplete,
  handDetector,
  objectDetector,
  detectionStatusMessage,
  isDetectionPreparing,
  detectionError,
  footer,
}) => {
  const {
    peerConnectionId,
    peerStatus,
    isPeerConnected,
    isSocketConnected,
    remoteStream,
    mobileHasJoined,
    lanStatus,
    lanStatusDetail,
    lanMatchedPeerFailed,
    isMobileBypassed,
    markMobileBypassed,
  } = useMobilePeerConnection();

  const resolvedPeerConnectionId = peerConnectionId ?? null;
  const [showNetworkModal, setShowNetworkModal] = useState(false);
  const [lanMismatchDismissed, setLanMismatchDismissed] = useState(false);
  const [mobileHasProceeded, setMobileHasProceeded] = useState(false);

  const baseConnectionUrl = useMemo(() => {
    if (connectionUrl) return connectionUrl;
    if (typeof window !== "undefined") return `${window.location.origin}/mobile-webcam`;
    return "/mobile-webcam";
  }, [connectionUrl]);

  const resolvedConnectionUrl = useMemo(() => {
    if (!resolvedPeerConnectionId) return baseConnectionUrl;
    const separator = baseConnectionUrl.includes("?") ? "&" : "?";

    if (typeof window === "undefined") {
      return `${baseConnectionUrl}${separator}peerConnectionId=${encodeURIComponent(resolvedPeerConnectionId)}`;
    }

    try {
      const url = new URL(baseConnectionUrl, window.location.origin);
      url.searchParams.set("peerConnectionId", resolvedPeerConnectionId);
      return url.toString();
    } catch (error) {
      console.error("[MobileWebCamSetup] Failed to append peerConnectionId to URL", error);
      return `${baseConnectionUrl}${separator}peerConnectionId=${encodeURIComponent(resolvedPeerConnectionId)}`;
    }
  }, [baseConnectionUrl, resolvedPeerConnectionId]);

  if (!resolvedPeerConnectionId) {
    return (
      <div className="rounded-xl border-2 border-dashed border-slate-200 bg-white p-8 text-center text-slate-600">
        <p>We could not generate a mobile connection ID. Refresh this page and try again.</p>
      </div>
    );
  }

  const effectivePeerConnected = isPeerConnected || isMobileBypassed;

  useEffect(() => {
    setShowNetworkModal(peerStatus === "error");
  }, [peerStatus]);

  // weh mobile has joined and socket is connected and mobile is not bypassed we are showing the webcam view
  const shouldShowWebcamView = mobileHasJoined && isSocketConnected && !isMobileBypassed;
  const isLanMismatch = lanStatus === "mismatched";
  const shouldShowNetworkModal = showNetworkModal || (isLanMismatch && !lanMismatchDismissed);
  const networkWarningReason = isLanMismatch ? "mismatch" : "network-error";

  const handleModalClose = () => {
    setShowNetworkModal(false);
    if (isLanMismatch) setLanMismatchDismissed(true);
  };

  useEffect(() => {
    if (!isLanMismatch) setLanMismatchDismissed(false);
  }, [isLanMismatch]);

  const handleProceed = () => {
    setMobileHasProceeded(true);
    onSetupComplete?.(true);
  };

  const handleBypassMobile = () => {
    setShowNetworkModal(false);
    setLanMismatchDismissed(true);
    markMobileBypassed(true);
    setMobileHasProceeded(true);
    onSetupComplete?.(true);
  };

  return (
    <>
      {/* state for mobile join and before connection*/}
      {!shouldShowWebcamView ? (
        <MobileWebCamBeforeConnection connectionUrl={resolvedConnectionUrl} footer={footer} />
      ) : (
        <MobileWebCamAfterConnection
          remoteStream={remoteStream}
          onProceed={handleProceed}
          connectionUrl={resolvedConnectionUrl}
          handDetector={handDetector}
          objectDetector={objectDetector}
          detectionStatusMessage={detectionStatusMessage}
          isDetectionPreparing={isDetectionPreparing}
          detectionError={detectionError}
          hasProceeded={mobileHasProceeded}
          footer={footer}
        />
      )}

      <MobileNetworkWarning
        open={shouldShowNetworkModal}
        reason={networkWarningReason}
        onClose={handleModalClose}
        networkDetails={isLanMismatch ? lanStatusDetail : null}
      />

      <MobileWebCamBypassModal
        open={lanMatchedPeerFailed && !mobileHasProceeded && !isMobileBypassed}
        onContinue={handleBypassMobile}
      />
    </>
  );
};

export default MobileWebCamSetup;
